/**
 * HISTORY FETCH do marketdata (bloco 4 — getBars OdinMD V3) — extraído de
 * marketdata.jsx na modularização dos god-objects. Contrato de URL/params,
 * seed do lastQuotes canônico e abort por chartId com identity-guard (#358 —
 * decisões puras em barsControllerDecisions.js). O Map de controllers
 * (`md._barsAbortControllers`) permanece NA INSTÂNCIA. Contrato pinado ANTES
 * da extração: marketdataIngressHistoryContract.test.js +
 * barsControllerDecisions.test.js.
 */
import * as utilities from "./utilites";
import timeoutManager from "../../utils/timeoutManager";
import { isOwnBarsController } from "./barsControllerDecisions.js";

const ODINMD_BARS_COMMAND = "/api/OdinMD/bars"; // V3 API

/**
 * Fetch historical bars for a symbol using OdinMD API (V3)
 *
 * @param {string} symbol - Symbol to fetch (e.g., "WINFUT", "PETR4")
 * @param {Date} suggestedStartDate - Start date for data range (not used by OdinMD, kept for compatibility)
 * @param {Date} suggestedEndDate - End date for data range
 * @param {Object} params - Query parameters
 * @param {string} params.periodicity - Periodicity: MINUTE, DAY, WEEK, MONTH, YEAR (uppercase)
 * @param {number} params.interval - Interval multiplier for MINUTE periodicity (1, 5, 15, 30, 60, etc.)
 * @param {number} params.barscount - Number of bars to fetch (1-16000)
 * @param {boolean} params.aftermarket - Include extended hours (default: false)
 * @param {boolean} params.adjusted - Apply corporate actions adjustments (default: true)
 * @returns {Promise<Object>} Response object with bars array (includes F, TC fields)
 *
 * Uses OdinMD V3 API with server-side caching (30-second cache for 99% faster repeat requests)
 */
// Track in-flight getBars requests per chart for abort support

// #358: libera o AbortController do getBars SÓ se ainda for o nosso. Um fetch mais
// novo (troca de símbolo/periodicidade no mesmo chartId) já o substituiu via set();
// deletar por chartId aqui apagaria o controller do fetch novo → ele nunca aborta e
// escreve barras de símbolo stale. Espelha o identity-guard de seamReconcilers
// (chartDataProcessor.js).
export const _releaseBarsController = (md, chartId, controller) => {
  if (isOwnBarsController(md._barsAbortControllers, chartId, controller)) {
    md._barsAbortControllers.delete(chartId);
  }
};

export const getBars = async (
  md,
  symbol,
  suggestedStartDate,
  suggestedEndDate,
  params,
) => {
  if (md.signalRToken === "") {
    timeoutManager.setTimeout(
      () => {
        // cb([]);
      },
      3000,
      "marketdata-getbars-token-wait",
    );
    return [];
  }

  // Abort previous in-flight request for this chart
  const chartId = params.chartId;
  let controller = null;
  if (chartId) {
    const prev = md._barsAbortControllers.get(chartId);
    if (prev) prev.abort();
    controller = new AbortController();
    md._barsAbortControllers.set(chartId, controller);
  }

  // OdinMD API uses uppercase periodicity and different parameter names
  const periodicity = params.periodicity?.toUpperCase() || "DAY";
  const interval = params.interval || 1;
  const barscount = params.barscount || 4000;
  const aftermarket =
    params.aftermarket !== undefined ? params.aftermarket : false;
  const adjusted = params.adjusted !== undefined ? params.adjusted : true;
  const date = suggestedEndDate.toISOString();

  // Build OdinMD API URL with new parameter names
  const serviceUrl = `${md.getUrl()}${ODINMD_BARS_COMMAND}?symbol=${symbol}&date=${date}&periodicity=${periodicity}&interval=${interval}&barscount=${barscount}&aftermarket=${aftermarket}&adjusted=${adjusted}`;

  const signal = chartId
    ? md._barsAbortControllers.get(chartId)?.signal
    : undefined;

  console.log(
    `[getBars] symbol=${symbol} periodicity=${periodicity} interval=${interval} barscount=${barscount} chartId=${chartId || "none"} date=${date}`,
  );
  try {
    const response = await utilities.getStringFromURL(
      serviceUrl,
      md.signalRToken,
      signal,
    );
    const ret = JSON.parse(response);

    // Response format is identical: { bars: [...], Status: 0 }
    // bars contain: { T, O, H, L, C, V, F, TC }
    if (ret.bars && ret.bars.length !== 0) {
      const lastPrice = ret.bars[ret.bars.length - 1].C;
      const canonical = md.getCanonicalSymbol(symbol);
      if (!md.lastQuotes.has(canonical)) {
        md.lastQuotes.set(canonical, {
          S: canonical,
          C: lastPrice,
          _receivedAt: Date.now(),
        });
      }
    }

    // Clean up controller after successful completion (#358: identity-guarded —
    // não apaga o controller de um fetch mais novo que já assumiu este chartId)
    md._releaseBarsController(chartId, controller);

    console.log(
      `[getBars] DONE symbol=${symbol} periodicity=${periodicity} interval=${interval} bars=${ret.bars?.length ?? 0}`,
    );
    return ret;
  } catch (error) {
    if (error.message?.includes("abort")) {
      console.log(
        `[getBars] ABORTED symbol=${symbol} periodicity=${periodicity} interval=${interval} chartId=${chartId || "none"}`,
      );
      return { bars: [], aborted: true };
    }
    console.log(
      `[getBars] ERROR symbol=${symbol} periodicity=${periodicity} interval=${interval}`,
      error,
    );
  }
};
